import { useRef, useState } from "react";
import { MdDelete, MdDownload, MdEdit } from "react-icons/md";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, FormModal, Input, SelectField, Textarea } from "../../components/ui";
import { formatDateFr } from "../../shared/dateFormat";
import { COLORS, RADIUS, SPACING } from "../../shared/theme";

const SCENARIO_TYPE = ["Panne matérielle", "Cyberattaque / Ransomware", "Incendie / Catastrophe naturelle", "Erreur humaine", "Défaillance fournisseur", "Pandémie / Indisponibilité RH", "Autre"];

export default function PagePRA({ pra, setPra, personnes }) {
  const [form, setForm] = useState(pra);
  const [editingScenario, setEditingScenario] = useState(null);
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [scenarioForm, setScenarioForm] = useState(null);
  const [showGlobalForm, setShowGlobalForm] = useState(false);
  const [globalFormDraft, setGlobalFormDraft] = useState(null);
  const nextIdScenario = useRef(10);

  const setScenario = k => e => setScenarioForm(f => ({ ...f, [k]: e.target.value }));
  const setGlobalDraft = k => e => setGlobalFormDraft(d => (d ? { ...d, [k]: e.target.value } : d));

  const openNewScenario = () => {
    setEditingScenario(null);
    setScenarioForm({ type: "Panne matérielle", description: "", procedures: "", rto: "", rpo: "", responsable: "", derniereMAJ: "" });
    setShowScenarioForm(true);
  };

  const openEditScenario = s => {
    setEditingScenario(s);
    setScenarioForm({ ...s });
    setShowScenarioForm(true);
  };

  const saveScenario = () => {
    setForm((f) => {
      let scenarios;
      if (editingScenario) {
        scenarios = f.scenarios.map(s => (s.id === editingScenario.id ? { ...scenarioForm, id: editingScenario.id } : s));
      } else {
        scenarios = [...f.scenarios, { ...scenarioForm, id: nextIdScenario.current++ }];
      }
      const next = { ...f, scenarios };
      setPra(next);
      return next;
    });
    setShowScenarioForm(false);
    setScenarioForm(null);
    setEditingScenario(null);
  };

  const openGlobalEdit = () => {
    setGlobalFormDraft({
      dateApprobation: form.dateApprobation ?? "",
      dateRevision: form.dateRevision ?? "",
      rtoGlobal: form.rtoGlobal ?? "",
      rpoGlobal: form.rpoGlobal ?? "",
      contactsUrgence: form.contactsUrgence ?? "",
    });
    setShowGlobalForm(true);
  };

  const saveGlobal = () => {
    if (!globalFormDraft) return;
    setForm((f) => {
      const next = { ...f, ...globalFormDraft };
      setPra(next);
      return next;
    });
    setShowGlobalForm(false);
    setGlobalFormDraft(null);
  };

  const displayOrDash = v => {
    const t = String(v ?? "").trim();
    return t !== "" ? t : "—";
  };

  const exportPdf = () => {
    const esc = t => String(t || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const scenariosHtml = (form.scenarios || []).map(s => {
      const resp = personnes.find(p => p.id === s.responsable || p.id === Number(s.responsable));
      return `<div style="margin-bottom:16px;padding:12px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px">
<h4 style="margin:0 0 6px;font-size:13px;color:#111">${esc(s.type)}</h4>
<p style="margin:0 0 8px;font-size:12px;color:#4b5563;line-height:1.5">${esc(s.description)}</p>
${s.procedures ? `<pre style="margin:8px 0 0;font-size:11px;white-space:pre-wrap;color:#6b7280">${esc(s.procedures)}</pre>` : ""}
<div style="margin-top:8px;font-size:11px;color:#374151"><strong>RTO :</strong> ${esc(s.rto)} &nbsp; <strong>RPO :</strong> ${esc(s.rpo)}${resp ? ` &nbsp; <strong>Resp :</strong> ${resp.prenom} ${resp.nom}` : ""}</div>
</div>`;
    }).join("");
    const testsHtml = (form.testsRealises || [])
      .map(
        (t) =>
          `<tr><td>${esc(t.date ? formatDateFr(t.date) : "")}</td><td>${esc(t.scenario)}</td><td>${esc(t.resultat)}</td><td>${esc(t.observations)}</td></tr>`
      )
      .join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>PRA - Plan de Reprise d'Activité</title></head><body style="font-family:system-ui,sans-serif;max-width:800px;margin:40px auto;padding:24px;color:#111">
<h1 style="margin:0 0 8px;font-size:22px;color:#7f1d1d">PRA — Plan de Reprise d'Activité</h1>
<p style="margin:0 0 24px;font-size:13px;color:#b91c1c">Scénarios de crise, procédures de reprise, RTO/RPO, tests</p>

<div style="margin-bottom:24px;padding:16px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px">
<h3 style="margin:0 0 12px;font-size:14px;color:#374151">Informations globales</h3>
<p style="margin:0;font-size:12px;color:#4b5563">Date approbation : ${esc(form.dateApprobation ? formatDateFr(form.dateApprobation) : "")} &nbsp; Révision : ${esc(form.dateRevision ? formatDateFr(form.dateRevision) : "")} &nbsp; RTO global : ${esc(form.rtoGlobal)} &nbsp; RPO global : ${esc(form.rpoGlobal)}</p>
${form.contactsUrgence ? `<pre style="margin:12px 0 0;font-size:11px;white-space:pre-wrap;color:#6b7280">${esc(form.contactsUrgence)}</pre>` : ""}
</div>

<h3 style="margin:0 0 12px;font-size:14px;color:#374151">Scénarios de crise</h3>
${scenariosHtml || "<p style='font-size:12px;color:#9ca3af'>Aucun scénario.</p>"}

${testsHtml ? `<h3 style="margin:24px 0 12px;font-size:14px;color:#374151">Tests réalisés</h3><table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr style="background:#f3f4f6"><th style="text-align:left;padding:8px;border:1px solid #e5e7eb">Date</th><th style="text-align:left;padding:8px;border:1px solid #e5e7eb">Scénario</th><th style="text-align:left;padding:8px;border:1px solid #e5e7eb">Résultat</th><th style="text-align:left;padding:8px;border:1px solid #e5e7eb">Observations</th></tr></thead><tbody>${testsHtml}</tbody></table>` : ""}
</body></html>`;
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => {
        win.print();
        win.onafterprint = () => win.close();
      }, 250);
    }
  };

  const pOptions = [{ value: "", label: "— Sélectionner —" }, ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom}` }))];

  return (
    <PageShell>
      <PageHeader
        title="PRA — Plan de Reprise d'Activité"
        subtitle="Scénarios de crise, procédures de reprise, RTO/RPO, tests"
      />

      <PageContent>
        {/* Infos globales — lecture seule + pop-in d’édition */}
        <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary }}>Informations globales du PRA</div>
            <button
              type="button"
              onClick={openGlobalEdit}
              title="Modifier les informations globales"
              style={{
                padding: `${SPACING.xs}px ${SPACING.sm}px`,
                borderRadius: RADIUS.sm,
                border: `1px solid ${COLORS.primaryAccent}`,
                background: "transparent",
                color: COLORS.primaryAccent,
                fontSize: 11,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <MdEdit size={14} />
            </button>
          </div>
          <div style={{ background: "#fafafa", borderRadius: RADIUS.md, padding: 14, border: `1px solid ${COLORS.border}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Date approbation</div>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>{form.dateApprobation ? formatDateFr(form.dateApprobation) : "—"}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Prochaine révision</div>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>{form.dateRevision ? formatDateFr(form.dateRevision) : "—"}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>RTO global</div>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>{displayOrDash(form.rtoGlobal)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>RPO global</div>
                <div style={{ fontSize: 13, color: COLORS.textPrimary }}>{displayOrDash(form.rpoGlobal)}</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Contacts d&apos;urgence</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{displayOrDash(form.contactsUrgence)}</div>
            </div>
          </div>

          {showGlobalForm && globalFormDraft && (
            <FormModal
              title="Modifier les informations globales du PRA"
              headerBackground="#7f1d1d"
              maxWidth={640}
              footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => { setShowGlobalForm(false); setGlobalFormDraft(null); }}
                    style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    Annuler
                  </button>
                  <button type="button" onClick={saveGlobal} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
                </div>
              }
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <FieldGroup label="Date approbation"><Input type="date" value={globalFormDraft.dateApprobation} onChange={setGlobalDraft("dateApprobation")} /></FieldGroup>
                <FieldGroup label="Prochaine révision"><Input type="date" value={globalFormDraft.dateRevision} onChange={setGlobalDraft("dateRevision")} /></FieldGroup>
                <FieldGroup label="RTO global"><Input value={globalFormDraft.rtoGlobal} onChange={setGlobalDraft("rtoGlobal")} placeholder="Ex : 24h" /></FieldGroup>
                <FieldGroup label="RPO global"><Input value={globalFormDraft.rpoGlobal} onChange={setGlobalDraft("rpoGlobal")} placeholder="Ex : 4h" /></FieldGroup>
                <div style={{ gridColumn: "span 2" }}>
                  <FieldGroup label="Contacts d'urgence">
                    <Textarea value={globalFormDraft.contactsUrgence} onChange={setGlobalDraft("contactsUrgence")} rows={4} placeholder="DSI, RSSI, Hébergeur, Assurance..." />
                  </FieldGroup>
                </div>
              </div>
            </FormModal>
          )}
        </div>

        {/* Scénarios */}
        <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 24, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary }}>Scénarios de crise ({form.scenarios.length})</div>
            <button type="button" onClick={openNewScenario} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Nouveau scénario</button>
          </div>

          {showScenarioForm && scenarioForm && (
            <FormModal
              title={editingScenario ? "Modifier le scénario" : "Nouveau scénario de crise"}
              headerBackground="#7f1d1d"
              maxWidth={760}
              footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => { setShowScenarioForm(false); setScenarioForm(null); setEditingScenario(null); }}
                    style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    Annuler
                  </button>
                  <button type="button" onClick={saveScenario} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
                </div>
              }
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <FieldGroup label="Type de scénario"><SelectField value={scenarioForm.type} onChange={setScenario("type")} options={SCENARIO_TYPE} /></FieldGroup>
                <FieldGroup label="Responsable"><SelectField value={scenarioForm.responsable} onChange={setScenario("responsable")} options={pOptions} /></FieldGroup>
                <div style={{ gridColumn: "span 2" }}><FieldGroup label="Description du scénario"><Textarea value={scenarioForm.description} onChange={setScenario("description")} rows={2} /></FieldGroup></div>
                <div style={{ gridColumn: "span 2" }}><FieldGroup label="Procédures de reprise (étapes)"><Textarea value={scenarioForm.procedures} onChange={setScenario("procedures")} rows={4} placeholder="1. ...\n2. ...\n3. ..." /></FieldGroup></div>
                <FieldGroup label="RTO (temps max reprise)"><Input value={scenarioForm.rto} onChange={setScenario("rto")} placeholder="Ex : 12h" /></FieldGroup>
                <FieldGroup label="RPO (perte données max)"><Input value={scenarioForm.rpo} onChange={setScenario("rpo")} placeholder="Ex : 1h" /></FieldGroup>
              </div>
            </FormModal>
          )}

          {form.scenarios.map(s => {
            const resp = personnes.find(p => p.id === s.responsable || p.id === Number(s.responsable));
            return (
              <div key={s.id} style={{ background: "#fafafa", borderRadius: RADIUS.md, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.textPrimary, marginBottom: 3 }}>{s.type}</div>
                    <div style={{ fontSize: 11, color: COLORS.textSecondary }}>{s.description}</div>
                  </div>
                  <div style={{ display: "flex", gap: 3 }}>
                    <button onClick={() => openEditScenario(s)} style={{ padding: `${SPACING.xs}px ${SPACING.sm}px`, borderRadius: RADIUS.sm, border: `1px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                    <button
                      onClick={() =>
                        setForm((f) => {
                          const next = { ...f, scenarios: f.scenarios.filter(sc => sc.id !== s.id) };
                          setPra(next);
                          return next;
                        })}
                      style={{ padding: "3px 8px", borderRadius: RADIUS.sm, border: "1px solid #dc2626", background: "transparent", color: "#dc2626", fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                    >
                      <MdDelete size={14} />
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#374151", marginTop: 6 }}>
                  <span><strong>RTO :</strong> {s.rto}</span>
                  <span><strong>RPO :</strong> {s.rpo}</span>
                  {resp && <span><strong>Resp :</strong> {resp.prenom} {resp.nom}</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={exportPdf} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "1.5px solid #93c5fd", background: "transparent", color: "#93c5fd", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}><MdDownload size={16} /> Exporter PDF</button>
        </div>
      </PageContent>
    </PageShell>
  );
}
